import {Injectable} from '@angular/core';
import {map} from 'rxjs/operators';
import {TApiResponseModel} from '../../typings/models/common/t-api-response-model';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {catchError, Observable, of} from 'rxjs';
import {environment} from '../../../environments/environment';
import {TApiResponseModelExtensions} from '../extensions/t-api-response-model.extensions';
import {
  TBaseBulkDeleteResponseDto,
  TBaseCreateResponseDto, TBaseDeleteResponseDto, TBaseGetAllResponseDto,
  TBaseGetResponseDto, TBaseLookupResponseDto,
  TBaseUpdateResponseDto
} from '../../typings/dtos/common/t-base-response.dto';
import {
  TBaseBulkDeleteRequestDto,
  TBaseCreateRequestDto, TBaseDeleteRequestDto, TBaseGetAllRequestDto,
  TBaseGetRequestDto, TBaseLookupRequestDto,
  TBaseUpdateRequestDto
} from '../../typings/dtos/common/t-base-request.dto';
import {TApiRequestModelExtensions} from '../extensions/t-api-request-model.extensions';
import {MapperService} from '../services/mapper.service';
import {TCommonModel, TListCommonModel} from '../../typings/models/common/t-common-model';
import {HTTP_PARAMS_CONSTANTS} from '../../core/constants/http/http-params.constants';
import {TSearchDto} from '../../typings/dtos/common/t-search-dto';
import {TPaginationRequestDto} from '../../typings/dtos/common/t-pagination.dto';
import {TPaginationResponseModel} from '../../typings/models/common/t-pagination.model';


// todo:
//  Automatic header injection (e.g., Authorization token)
// Global logging / retry mechanisms
// Error translation (turning backend messages into user-friendly messages)
// Loading indicators
@Injectable({
  providedIn: 'root',
})
export class CommonHttpProviderService {
  constructor(
    private http: HttpClient,
    private readonly mapper: MapperService
  ) {
  }

  private environment = environment;

  private url(action: string, dtos: any[] | null): string {
    let fullUrl: string = `${this.environment.apiBaseUrl}/${action}`;

    return TApiRequestModelExtensions.buildUrlFromModel(fullUrl, dtos);
  }

  private generateHeaders(headers?: HttpHeaders | null): HttpHeaders {
    let newHeaders = headers || new HttpHeaders();

    return newHeaders;
  }

  getAll<TRequestDto extends TBaseGetAllRequestDto,
    TResponseDto extends TBaseGetAllResponseDto,
    TRequestModel extends TListCommonModel,
    TResponseModel extends TListCommonModel>(
    tRequestDtoClass: (new () => TRequestDto) | null,
    tResponseDtoClass: (new () => TResponseDto) | null,
    tRequestModelClass: (new () => TRequestModel) | null,
    tResponseModelClass: (new () => TResponseModel) | null,
    action: string,
    paginationRequestDto: TPaginationRequestDto,
    tSearchDto: TSearchDto | null,
    headers?: HttpHeaders | null,
    doResponseMapping: boolean = true,
  ): Observable<TApiResponseModel<TPaginationResponseModel<TResponseModel | TResponseDto> | null>> {
    return this.http.get<TApiResponseModel<TPaginationResponseModel<TResponseDto>>>(
      this.url(action, [paginationRequestDto, tSearchDto]),
      {headers: this.generateHeaders(headers)}
    ).pipe(
      map((res) => {
        if (!res.success) {
          return TApiResponseModelExtensions.replaceData(res, null);
        }

        if (!doResponseMapping) {
          return res;
        }

        const tResponse = res.data?.items?.map(item =>
          this.mapper.map(item, tResponseDtoClass!, tResponseModelClass!)
        ) ?? null;

        return TApiResponseModelExtensions.replaceListData(res, tResponse);
      }),
      catchError((error): Observable<TApiResponseModel<TPaginationResponseModel<TResponseModel> | null>> => {
        return this.handleHttpError(error);
      })
    );
  }

  get<TRequestDto extends TBaseGetRequestDto,
    TResponseDto extends TBaseGetResponseDto,
    TRequestModel extends TCommonModel,
    TResponseModel extends TCommonModel>(
    tRequestDtoClass: (new () => TRequestDto) | null,
    tResponseDtoClass: (new () => TResponseDto) | null,
    tRequestModelClass: (new () => TRequestModel) | null,
    tResponseModelClass: (new () => TResponseModel) | null,
    action: string,
    dto: TBaseGetRequestDto | null,
    headers?: HttpHeaders | null,
    doResponseMapping: boolean = true,
  ): Observable<TApiResponseModel<TResponseDto | TResponseModel | null>> {
    return this.http.get<TApiResponseModel<TResponseDto | null>>(
      this.url(action, !dto ? null : [dto]),
      {headers: this.generateHeaders(headers)}
    ).pipe(
      map((res) => {
        if (!res.success) {
          return TApiResponseModelExtensions.replaceData(res, null);
        }

        if (!doResponseMapping) {
          return res;
        }

        return TApiResponseModelExtensions
          .replaceData(res, this.mapper.map(res.data, tResponseDtoClass!, tResponseModelClass!));
      }),
      catchError((error): Observable<TApiResponseModel<TResponseModel | null>> => {
        return this.handleHttpError(error);
      })
    );
  }

  getById<TRequestDto extends TBaseGetRequestDto,
    TResponseDto extends TBaseGetResponseDto,
    TRequestModel extends TCommonModel,
    TResponseModel extends TCommonModel>(
    tRequestDtoClass: (new () => TRequestDto) | null,
    tResponseDtoClass: (new () => TResponseDto) | null,
    tRequestModelClass: (new () => TRequestModel) | null,
    tResponseModelClass: (new () => TResponseModel) | null,
    action: string,
    headers?: HttpHeaders | null,
    id?: string,
    doResponseMapping: boolean = true,
  ): Observable<TApiResponseModel<TResponseDto | TResponseModel | null>> {
    return this.http.get<TApiResponseModel<TResponseDto | null>>(
      this.url(action + `/${id}`, null),
      {headers: this.generateHeaders(headers)}
    ).pipe(
      map((res) => {
        if (!res.success) {
          return TApiResponseModelExtensions.replaceData(res, null);
        }

        if (!doResponseMapping) {
          return res;
        }

        return TApiResponseModelExtensions
          .replaceData(res, this.mapper.map(res.data, tResponseDtoClass!, tResponseModelClass!));
      }),
      catchError((error): Observable<TApiResponseModel<TResponseModel | null>> => {
        return this.handleHttpError(error);
      })
    );
  }

  getLookup<TRequestDto extends TBaseLookupRequestDto,
    TResponseDto extends TBaseLookupResponseDto,
    TRequestModel extends TCommonModel,
    TResponseModel extends TCommonModel>(
    tRequestDtoClass: (new () => TRequestDto) | null,
    tResponseDtoClass: (new () => TResponseDto) | null,
    tRequestModelClass: (new () => TRequestModel) | null,
    tResponseModelClass: (new () => TResponseModel) | null,
    action: string,
    tRequestDto: TRequestDto | null,
    headers?: HttpHeaders | null,
    doResponseMapping: boolean = false,
  ): Observable<TApiResponseModel<TResponseDto[] | TResponseModel[] | null>> {
    return this.http.get<TApiResponseModel<TResponseDto[] | null>>(
      this.url(action, [tRequestDto]),
      {headers: this.generateHeaders(headers)}
    ).pipe(
      map((res) => {
        if (!res.success) {
          return TApiResponseModelExtensions.replaceData(res, null);
        }

        if (!doResponseMapping) {
          return res;
        }

        const tResponse = res.data?.map(item =>
          this.mapper.map(item, tResponseDtoClass!, tResponseModelClass!)
        ) ?? null;

        return TApiResponseModelExtensions.replaceData(res, tResponse);
      }),
      catchError((error): Observable<TApiResponseModel<TResponseModel[] | null>> => {
        return this.handleHttpError(error);
      })
    );
  }

  post<TRequestDto extends TBaseCreateRequestDto,
    TResponseDto extends TBaseCreateResponseDto,
    TRequestModel extends TCommonModel,
    TResponseModel extends TCommonModel>(
    tRequestDtoClass: (new () => TRequestDto) | null,
    tResponseDtoClass: (new () => TResponseDto) | null,
    tRequestModelClass: (new () => TRequestModel) | null,
    tResponseModelClass: (new () => TResponseModel) | null,
    action: string,
    headers?: HttpHeaders | null,
    tRequestModel?: TRequestModel | null,
    tRequestDto?: TRequestDto | null,
    doRequestMapping: boolean = true,
    doResponseMapping: boolean = true,
    bodyType: 'json' | 'form-data' = 'json',
  ): Observable<TApiResponseModel<TResponseDto | TResponseModel | null>> {
    return this.http.post<TApiResponseModel<TResponseDto | null>>(
      this.url(action, null),
      !doRequestMapping ? tRequestDto : this.mapper.map(tRequestModel, tRequestModelClass!, tRequestDtoClass!),
      {headers: this.generateHeaders(headers!)}
    ).pipe(
      map((res) => {
        if (!res.success) {
          return TApiResponseModelExtensions.replaceData(res, null);
        }

        if (!doResponseMapping) {
          return res;
        }

        return TApiResponseModelExtensions
          .replaceData(res, this.mapper.map(res.data, tResponseDtoClass!, tResponseModelClass!));
      }),
      catchError((error): Observable<TApiResponseModel<TResponseModel | null>> => {
        return this.handleHttpError(error);
      })
    );
  }

  patch<TRequestDto extends TBaseUpdateRequestDto,
    TResponseDto extends TBaseUpdateResponseDto,
    TRequestModel extends TCommonModel,
    TResponseModel extends TCommonModel>(
    tRequestDtoClass: (new () => TRequestDto) | null,
    tResponseDtoClass: (new () => TResponseDto) | null,
    tRequestModelClass: (new () => TRequestModel) | null,
    tResponseModelClass: (new () => TResponseModel) | null,
    action: string,
    headers?: HttpHeaders | null,
    tRequestModel?: TRequestModel | null,
    tRequestDto?: TRequestDto | null,
    doRequestMapping: boolean = true,
    doResponseMapping: boolean = true,
  ): Observable<TApiResponseModel<TResponseDto | TResponseModel | null>> {
    return this.http.patch<TApiResponseModel<TResponseDto | null>>(
      this.url(action, null),
      !doRequestMapping ? tRequestDto : this.mapper.map(tRequestModel, tRequestModelClass!, tRequestDtoClass!),
      {headers: this.generateHeaders(headers!)}
    ).pipe(
      map((res) => {
        if (!res.success) {
          return TApiResponseModelExtensions.replaceData(res, null);
        }

        if (!doResponseMapping) {
          return res;
        }

        return TApiResponseModelExtensions
          .replaceData(res, this.mapper.map(res.data, tResponseDtoClass!, tResponseModelClass!));
      }),
      catchError((error): Observable<TApiResponseModel<TResponseModel | null>> => {
        return this.handleHttpError(error);
      })
    );
  }

  delete<
    TRequestDto extends TBaseDeleteRequestDto,
    TResponseDto extends TBaseDeleteResponseDto,
    TRequestModel extends TCommonModel,
    TResponseModel extends TCommonModel
  >(
    tRequestDtoClass: (new () => TRequestDto) | null,
    tResponseDtoClass: (new () => TResponseDto) | null,
    tRequestModelClass: (new () => TRequestModel) | null,
    tResponseModelClass: (new () => TResponseModel) | null,
    action: string,
    id: string,
    headers?: HttpHeaders | null
  ): Observable<TApiResponseModel<TResponseDto | TResponseModel | null>> {
    return this.http.delete<TApiResponseModel<TResponseDto | null>>(
      this.url(
        action + `/${id}`,
        null
      ),
      {headers: this.generateHeaders(headers)}
    ).pipe(
      map((res) => {
        if (!res.success)
          return TApiResponseModelExtensions.replaceData(res, null);

        return TApiResponseModelExtensions.replaceData(res, null);
      }),
      catchError((error): Observable<TApiResponseModel<TResponseModel | null>> => {
        return this.handleHttpError(error);
      })
    );
  }

  bulkDelete<
    TRequestDto extends TBaseBulkDeleteRequestDto,
    TResponseDto extends TBaseBulkDeleteResponseDto,
    TRequestModel extends TCommonModel,
    TResponseModel extends TCommonModel
  >(
    tRequestDtoClass: (new () => TRequestDto) | null,
    tResponseDtoClass: (new () => TResponseDto) | null,
    tRequestModelClass: (new () => TRequestModel) | null,
    tResponseModelClass: (new () => TResponseModel) | null,
    action: string,
    dto: TBaseBulkDeleteRequestDto,
    headers?: HttpHeaders | null
  ): Observable<TApiResponseModel<TResponseDto | TResponseModel | null>> {
    return this.http.delete<TApiResponseModel<TResponseDto | null>>(
      this.url(
        action,
        null
      ),
      {headers: this.generateHeaders(headers), body: dto.ids}
    ).pipe(
      map((res) => {
        if (!res.success)
          return TApiResponseModelExtensions.replaceData(res, null);

        return TApiResponseModelExtensions.replaceData(res, null);
      }),
      catchError((error): Observable<TApiResponseModel<TResponseModel | null>> => {
        return this.handleHttpError(error);
      })
    );
  }

  private handleHttpError<TModel>(error: any): Observable<TApiResponseModel<TModel | null>> {
    console.error('HTTP request failed: ', error);

    return of({
      success: false,
      message: error?.error?.message || 'Unexpected error',
      data: null,
      errors: error?.error?.errors || [],
      timestamp: error?.error?.timeStamp ?? new Date()
    } as TApiResponseModel<TModel | null>);
  }
}
